import { useMemo, useState } from 'react';
import {
	IMessageEventFormMetadata,
	IMessageEventFormPayload,
} from '@enuan/chat__utils';
import { Collapsible } from '../../UI/Collapsible/Collapsible';
import { useParseText } from '../../../hooks/useParseText';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import { fontSecondaryOblique } from 'src/theme/mixins';

type ISummaryProps = {
	data: IMessageEventFormPayload;
	metadata: IMessageEventFormMetadata;
	className?: string;
	isOpen?: boolean;
	toggable?: boolean;
	onToggle?: (isOpen: boolean) => void;
};

export const Summary = styled(
	({
		data: formValues,
		metadata: { labels },
		isOpen: _isOpen,
		toggable = true,
		className,
	}: ISummaryProps) => {
		const [isOpen, setOpen] = useState(!toggable || _isOpen);

		const {
			header: { title = undefined, summary_title = undefined } = {},
			form: fields,
		} = labels ?? {};

		const { html: title_html, text: title_text } = useParseText(
			summary_title ?? title,
		);

		return (
			<Collapsible
				isOpen={isOpen}
				className={classNames('collapsible--summary', className)}
				toggle={toggable ? () => setOpen(!isOpen) : undefined}
				header={
					<div
						dangerouslySetInnerHTML={{ __html: title_html ?? '' }}
						title={title_text}
					/>
				}
			>
				<div className="list">
					{fields?.map(({ label, id, value }) => (
						<div key={id} className="list__item">
							<SummaryItem
								label={label ?? id}
								value={value ?? formValues[id]}
							/>
						</div>
					))}
				</div>
			</Collapsible>
		);
	},
	{
		target: 'Summary-root',
		label: 'Summary',
	},
)({
	'& .list': {
		display: 'flex',
		flexDirection: 'column',

		'& .list__item': {
			marginBottom: '0.5em',
		},
	},
});

type ISummaryItemProps = {
	label: string;
	value: string | string[];
	className?: string;
};

const SummaryItem = styled(
	({ label, value, className }: ISummaryItemProps) => {
		const values = useMemo(() => {
			let _values: string[] = [];
			if (!Array.isArray(value)) _values = [value];
			else _values = value;

			const __values = _values.map((v) =>
				typeof v === 'undefined' || v === '' ? '-' : v,
			);
			return __values.map((e, i) => <span key={i}>{e}</span>);
		}, [value]);

		return (
			<div className={classNames('item', className)}>
				<span className="item__label">{label}</span>
				<div className="item__value">{values}</div>
			</div>
		);
	},
	{
		target: 'SummaryItem-root',
		label: 'SummaryItem',
	},
)({
	display: 'flex',
	justifyContent: 'space-between',
	'& .item__label': {
		maxWidth: '50%',
		fontFamily: "'Ubuntu', sans-serif",
		fontSize: '1.3em',
		paddingRight: '1em',
		color: 'var(--message__summary__item__label_color)',
	},
	'& .item__value': {
		maxWidth: '50%',
		textAlign: 'right',
		color: 'var(--message__summary__item__value_color)',

		'& > span': {
			...fontSecondaryOblique,
			fontSize: '1.3em',
			wordBreak: 'break-word',

			'&:not(:last-child)': {
				paddingRight: '0.2em',
				'&::after': {
					content: '", "',
					fontSize: '1.3em',
					display: 'inline-block',
				},
			},
		},
	},
});
